"use client";

import {motion} from "framer-motion";
import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {ChatView} from "@/components/chat";
import {useArtifactPanel} from "@/contexts/chat/ArtifactPanelContext";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import {
   type OptimisticMessage,
   useChatTransfer,
} from "@/contexts/common/ChatTransferContextProvider";
import {Permissions, RoleData} from "@/enums/user";
import {useAlert} from "@/hooks/useAlert";
import {useAssistantModelUpdate} from "@/hooks/useAssistantModelUpdate";
import {useBanner} from "@/hooks/useBanner";
import {useChat} from "@/hooks/useChat";
import {isAutoSelectableForConversation} from "@/services/wingman";
import {isAutoAssistantModel} from "@/utils/ai/modelCatalog";

import {useOrg} from "@/hooks/useOrg";
import {usePermissions} from "@/hooks/usePermissions";
import {useUserProfile} from "@/hooks/useUserProfile";
import {useSkills} from "@/hooks/useSkills";
import ChatSkeleton from "./ChatSkeleton";

interface ChatViewContainerProps {
   orgId?: string;
   conversationId: string;
   /** Fill the parent element instead of viewport-height layout (dock mode). */
   fillContainer?: boolean;
}

const getOptimisticMessage = (payload: {message: OptimisticMessage}): OptimisticMessage => ({
   text: payload.message.text,
   fileData: payload.message.fileData || [],
   assistantId: payload.message.assistantId,
});

const ChatViewContainer = ({orgId, conversationId, fillContainer}: ChatViewContainerProps) => {
   const {isBannerActive} = useBanner();
   const {setAlert} = useAlert();
   const {ui} = useOrg();
   const {skills} = useSkills();
   const {userProfile} = useUserProfile();
   const {canAccess} = usePermissions();
   const {t} = useTranslation("ask");
   const {t: ta} = useTranslation("assistants");
   const canEditAssistants = canAccess(RoleData.Assistants, Permissions.Update);

   const {consumeOptimisticTransfer, peekOptimisticTransfer} = useChatTransfer();
   const pendingOptimisticTransfer = peekOptimisticTransfer(conversationId);
   const initialOptimisticTransferRef = useRef(pendingOptimisticTransfer);

   const [isLoadingConversation, setIsLoadingConversation] = useState(!pendingOptimisticTransfer);
   const [conversationAssistantId, setConversationAssistantId] = useState<string | null>(
      () => pendingOptimisticTransfer?.assistantId ?? null
   );
   const [optimisticMessage, setOptimisticMessage] = useState<OptimisticMessage | null>(() =>
      pendingOptimisticTransfer ? getOptimisticMessage(pendingOptimisticTransfer) : null
   );
   const hasResumedFromQueryRef = useRef(false);

   const {
      messages,
      inputValue,
      setInputValue,
      isSubmitting,
      streamingMessageId,
      selectedAssistant,
      isLoadingAssistants,
      displayAssistants,
      displaySelectedAssistant,
      handleAssistantChange,
      updateAssistantData,
      handleSendMessage,
      handleToolApprovalResponse,
      handleFilesSelected,
      handleFilesChange,
      handleInvalidFiles,
      attachedFiles,
      fileUploadConfig,
      convertToChatMessages,
      loadExistingConversation,
      canSendMessages,
      handleFeedback,
      cancelStream,
      tokenLimitReached,
      tokenUsagePercent,
      tokenBreakdown,
      streamingStatus,
   } = useChat({
      orgId,
      conversationId,
      initialAssistantId: conversationAssistantId,
      optimisticMessage,
      attachPageContext: fillContainer,
   });

   const {updateModel, isSaving: isModelSaving} = useAssistantModelUpdate({
      onModelUpdated: (assistantId, newModel) => {
         updateAssistantData(assistantId, {model: newModel});
      },
   });
   const handleModelChange = (assistantId: string, newModel: string) => {
      if (isAutoAssistantModel(newModel) && !isAutoSelectableForConversation(conversationId)) {
         setAlert({
            status: "warning",
            message: t("autoUnavailableOnLegacyConversation"),
            secondsToClose: 10,
         });
         return Promise.resolve(false);
      }
      return updateModel(assistantId, newModel);
   };

   // Let workspace-panel actions (a sibling of this chat view) post outcomes
   // into the conversation, e.g. "I saved the script as ...". Ref-bridged
   // because handleSendMessage is recreated each render.
   const {registerChatMessageSender} = useArtifactPanel();
   const {sendDockFollowUp} = useGailDock();
   const sendMessageRef = useRef(handleSendMessage);
   sendMessageRef.current = handleSendMessage;
   useEffect(() => {
      registerChatMessageSender((text) => {
         void sendMessageRef.current(text);
      });
      return () => registerChatMessageSender(null);
   }, [registerChatMessageSender]);

   // A script save hands the conversation to the dock, then this surface sends
   // the save note. Sending it on the full-page chat would abort when that
   // view unmounts during the route change. Wait until the dock can actually
   // send, and leave the note queued if it can't yet.
   useEffect(() => {
      if (!fillContainer || isLoadingConversation || isLoadingAssistants || isSubmitting || tokenLimitReached) return;
      if (!canSendMessages || !selectedAssistant || selectedAssistant.id === "loading") return;
      // useChat reports send failures; keep the note queued if a sender rejects.
      void sendDockFollowUp(conversationId, text => sendMessageRef.current(text)).catch(() => {});
   }, [
      fillContainer,
      isLoadingConversation,
      isLoadingAssistants,
      isSubmitting,
      canSendMessages,
      selectedAssistant,
      conversationId,
      sendDockFollowUp,
      tokenLimitReached,
   ]);

   const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

   // Memoize converted messages to avoid re-processing on every keystroke
   const convertedMessages = useMemo(
      () => convertToChatMessages(messages),
      [messages, convertToChatMessages]
   );
   const latestMessage = messages.at(-1);
   const activeToolApprovalMessageId = latestMessage?.role === "assistant" && !latestMessage.synthetic
      ? latestMessage.id : undefined;

   // Check for optimistic conversation data from in-memory transfer
   useEffect(() => {
      const payload = consumeOptimisticTransfer(conversationId);

      // The first-render transfer already seeded state. Only write state when
      // a later conversation supplies a different transfer; replacing the
      // seeded message would needlessly replay useChat's send effect.
      if (payload && payload !== initialOptimisticTransferRef.current) {
         setOptimisticMessage(getOptimisticMessage(payload));

         setConversationAssistantId(payload.assistantId);
         setIsLoadingConversation(false);
      }
   }, [conversationId, consumeOptimisticTransfer]);

   useEffect(() => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];

      const initializeConversation = async () => {
         // If we have optimistic message data, skip loading existing conversation
         if (optimisticMessage) {
            return;
         }

         // If we already have messages (from streaming), don't reload from server
         if (messages.length > 0) {
            setIsLoadingConversation(false);
            return;
         }

         setIsLoadingConversation(true);

         try {
            const result = await loadExistingConversation();

            if (!result?.conversation) {
               console.error(
                  "Failed to load conversation details - cannot proceed with loading messages"
               );
               setAlert({
                  status: "error",
                  message: t("failedToLoadConversation"),
                  secondsToClose: 10,
               });
               return;
            }

            setConversationAssistantId(result.conversation.assistantId);
         } catch (error) {
            console.error("Error initializing conversation:", error);
            setAlert({
               status: "error",
               message: t("failedToLoadConversation"),
               secondsToClose: 10,
            });
         } finally {
            setIsLoadingConversation(false);
         }
      };

      initializeConversation();

      return () => {
         timeoutsRef.current.forEach(clearTimeout);
      };
   }, [conversationId, orgId, optimisticMessage]);

   useEffect(() => {
      if (hasResumedFromQueryRef.current || typeof window === "undefined") return;
      if (isLoadingConversation || isLoadingAssistants || isSubmitting) return;
      if (!canSendMessages || !selectedAssistant || selectedAssistant.id === "loading") return;

      const params = new URLSearchParams(window.location.search);
      const input = params.get("input");
      const prefill = params.get("prefill");
      if (!input && !prefill) return;

      hasResumedFromQueryRef.current = true;
      const url = new URL(window.location.href);
      url.searchParams.delete("input");
      url.searchParams.delete("prefill");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);

      if (input?.trim()) {
         void handleSendMessage(input);
         return;
      }
      if (prefill) setInputValue(prefill);
   }, [
      canSendMessages,
      handleSendMessage,
      isLoadingAssistants,
      isLoadingConversation,
      isSubmitting,
      selectedAssistant,
      setInputValue,
   ]);

   if (!isLoadingAssistants && (!selectedAssistant || selectedAssistant.id === "loading")) {
      return (
         <div className="flex justify-center items-center h-full">
            <div className="text-center">
               <h3 className="mb-2 font-semibold text-lg">{ta("noAssistants")}</h3>
               <p className="text-gray-600">{ta("noChatHistory")}</p>
            </div>
         </div>
      );
   }
   // Show loading only if we're not in optimistic mode and haven't loaded optimistic data yet
   if (isLoadingConversation && !optimisticMessage) {
      return (
         <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.3}}
            className="h-full"
         >
            <ChatSkeleton fillContainer={fillContainer} />
         </motion.div>
      );
   }

   let disabled = !canSendMessages || selectedAssistant?.status !== "active" || tokenLimitReached;

   let inputPlaceholder = t("reply");
   if (disabled && tokenLimitReached) {
      inputPlaceholder = t("tokenLimitReached");
   } else if (disabled) {
      inputPlaceholder = t("cannotSendMessages");
   }

   return (
      <motion.div
         initial={{opacity: 0, y: 6}}
         animate={{opacity: 1, x: 0, y: 0}}
         transition={{duration: 0.35, ease: [0.16, 1, 0.3, 1], delay: 0.05}}
         className="h-full"
      >
         <ChatView
            skills={skills}
            conversationId={conversationId}
            messages={convertedMessages}
            inputValue={inputValue}
            onInputChange={setInputValue}
            onSendMessage={handleSendMessage}
            onToolApprovalResponse={handleToolApprovalResponse}
            activeToolApprovalMessageId={activeToolApprovalMessageId}
            isSubmitting={isSubmitting}
            onCancelStream={cancelStream}
            disabled={disabled}
            isBannerActive={isBannerActive}
            senderName={selectedAssistant?.name ?? "Assistant"}
            assistants={displayAssistants}
            selectedAssistant={displaySelectedAssistant || undefined}
            onAssistantChange={isLoadingAssistants ? () => {} : handleAssistantChange}
            inputPlaceholder={inputPlaceholder}
            brand={ui.brand}
            theme={userProfile.theme}
            onFilesSelected={handleFilesSelected}
            attachedFiles={attachedFiles}
            onFilesChange={handleFilesChange}
            fileUploadConfig={fileUploadConfig}
            onInvalidFiles={handleInvalidFiles}
            isLoadingAssistants={isLoadingAssistants}
            fileCompatibilityTooltip={t("assistantDoesNotSupportFilesTooltip")}
            isStreaming={streamingMessageId !== null}
            streamingStatus={streamingStatus}
            onFeedback={handleFeedback}
            canEditModel={canEditAssistants}
            onModelChange={handleModelChange}
            isModelSaving={isModelSaving}
            tokenLimitReached={tokenLimitReached}
            tokenUsagePercent={tokenUsagePercent}
            tokenBreakdown={tokenBreakdown}
            fillContainer={fillContainer}
         />
      </motion.div>
   );
};

export default ChatViewContainer;
